/* eslint-disable react-hooks/exhaustive-deps */
// Iter52 P9 — Admin page: Reguli clasificare produse.
//
// Self-service mapping of unknown category values (e.g. "PPC RELAXAT", a new
// ENGIE offer) to a product kind (EE/Gaz/VAS/HVAS/Anulat). Rules override the
// builtin heuristics in the importers — no code changes required.
//
// Created via click-to-map from the import banner OR directly from this page.

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Sparkles, Plus, Trash2, ArrowRight, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const KIND_OPTIONS = [
  // Iter52 P13g — Aligned 1:1 with Products & Offers page labels.
  // "Anulat" was removed: cancellation is a STATUS, not a product category.
  { value: "EE", label: "EE — Energie electrică" },
  { value: "Gaz", label: "Gaz" },
  { value: "VAS", label: "VAS — Valoare Adăugată Servicii" },
  { value: "HVAS", label: "HVAS — Hard VAS (echipamente)" },
  { value: "Combo EE + VAS", label: "Combo EE + VAS — Pachet Energie + Servicii" },
  { value: "Combo Gaz + VAS", label: "Combo Gaz + VAS — Pachet Gaz + Servicii" },
];

const SOURCE_OPTIONS = [
  { value: "all", label: "Toate sursele" },
  { value: "salesforce", label: "Salesforce" },
  { value: "crm", label: "CRM legacy" },
  { value: "easy", label: "Easy" },
];

const KIND_BADGE_COLOR = {
  EE: "bg-amber-100 text-amber-800 border-amber-300",
  Gaz: "bg-blue-100 text-blue-800 border-blue-300",
  VAS: "bg-emerald-100 text-emerald-800 border-emerald-300",
  HVAS: "bg-teal-100 text-teal-800 border-teal-300",
  // Iter52 P13g — Combo offers (aligned with Products page label)
  "Combo EE + VAS": "bg-indigo-100 text-indigo-800 border-indigo-300",
  "Combo Gaz + VAS": "bg-cyan-100 text-cyan-800 border-cyan-300",
};

export default function AdminClassificationRules() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterSource, setFilterSource] = useState("all");
  const [onlyActive, setOnlyActive] = useState(false);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    pattern: "",
    match_type: "exact",
    kind: "EE",
    source: "all",
    active: true,
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (filterSource && filterSource !== "all") params.set("source", filterSource);
      if (onlyActive) params.set("only_active", "true");
      const r = await axios.get(`${API}/api/admin/product-classification-rules?${params.toString()}`);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la încărcare");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [search, filterSource, onlyActive]);

  const openCreate = () => {
    setForm({
      pattern: "",
      match_type: "exact",
      kind: "EE",
      source: "all",
      active: true,
      notes: "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.pattern.trim()) {
      toast.error("Pattern obligatoriu");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/admin/product-classification-rules`, {
        pattern: form.pattern.trim(),
        match_type: form.match_type,
        kind: form.kind,
        source: form.source,
        active: form.active,
        notes: form.notes.trim() || null,
      });
      toast.success("Regulă creată");
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item) => {
    try {
      await axios.patch(`${API}/api/admin/product-classification-rules/${item.id}`, {
        active: !item.active,
      });
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la activare/dezactivare");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Ștergi această regulă? Acțiune ireversibilă.")) return;
    try {
      await axios.delete(`${API}/api/admin/product-classification-rules/${id}`);
      toast.success("Regulă ștearsă");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la ștergere");
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6" data-testid="admin-classification-rules">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
            <Link to="/admin" className="hover:underline">Admin</Link>
            <ArrowRight className="h-3 w-3" />
            <span>Reguli clasificare produse</span>
          </div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-indigo-600" />
            Reguli clasificare produse
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Mapează valorile necunoscute din importuri (ex: „PPC RELAXAT", „RELAX VERDE 12")
            la tipuri de produs (EE/Gaz/VAS/HVAS). Regulile au prioritate față de euristicile
            din cod și se aplică imediat la următorul preflight/simulate/import — fără rebuild.
          </p>
        </div>
        {isAdmin && (
          <Button onClick={openCreate} data-testid="add-rule-btn">
            <Plus className="h-4 w-4 mr-2" /> Adaugă regulă
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Reguli active</CardTitle>
          <CardDescription>
            {items.length} regulă{items.length === 1 ? "" : "i"} {filterSource !== "all" ? `pentru sursa „${filterSource}"` : "în total"}.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[240px]">
              <Label className="text-xs">Caută</Label>
              <div className="relative mt-1">
                <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
                <Input
                  className="pl-9"
                  placeholder="pattern sau note…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="rule-search"
                />
              </div>
            </div>
            <div className="min-w-[180px]">
              <Label className="text-xs">Sursă</Label>
              <Select value={filterSource} onValueChange={setFilterSource}>
                <SelectTrigger className="mt-1" data-testid="rule-filter-source">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm pb-2 cursor-pointer">
              <input
                type="checkbox"
                checked={onlyActive}
                onChange={(e) => setOnlyActive(e.target.checked)}
                data-testid="rule-filter-active"
              />
              Doar active
            </label>
          </div>

          {/* Table */}
          {loading ? (
            <p className="text-sm text-slate-500">Se încarcă…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-slate-500 italic">
              Nicio regulă încă. Adaugă una manual sau folosește click-to-map din banner-ul de import.
            </p>
          ) : (
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2">Pattern</th>
                    <th className="text-center px-3 py-2">Mod</th>
                    <th className="text-center px-3 py-2">Kind</th>
                    <th className="text-center px-3 py-2">Sursă</th>
                    <th className="text-center px-3 py-2">Activă</th>
                    <th className="text-left px-3 py-2">Note</th>
                    <th className="text-right px-3 py-2">Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100" data-testid={`rule-row-${r.id}`}>
                      <td className="px-3 py-2 font-mono text-[12px]">{r.pattern}</td>
                      <td className="px-3 py-2 text-center text-xs">
                        <Badge variant="outline">{r.match_type}</Badge>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border ${KIND_BADGE_COLOR[r.kind] || ""}`}>
                          {r.kind}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center text-xs font-mono">{r.source}</td>
                      <td className="px-3 py-2 text-center">
                        {isAdmin ? (
                          <button
                            onClick={() => toggleActive(r)}
                            className={`text-xs font-semibold ${r.active ? "text-emerald-700" : "text-slate-400"}`}
                            data-testid={`rule-toggle-${r.id}`}
                          >
                            {r.active ? "✓ activă" : "○ inactivă"}
                          </button>
                        ) : (
                          <span className={`text-xs ${r.active ? "text-emerald-700" : "text-slate-400"}`}>
                            {r.active ? "✓ activă" : "○ inactivă"}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600">{r.notes || "—"}</td>
                      <td className="px-3 py-2 text-right">
                        {isAdmin && (
                          <button
                            onClick={() => handleDelete(r.id)}
                            className="text-rose-600 hover:text-rose-800"
                            data-testid={`rule-delete-${r.id}`}
                            title="Șterge"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="rule-create-dialog">
          <DialogHeader>
            <DialogTitle>Adaugă regulă</DialogTitle>
            <DialogDescription>
              Mapează o valoare brută (ex: nume ofertă) la un tip de produs.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Pattern (valoarea brută)</Label>
              <Input
                value={form.pattern}
                onChange={(e) => setForm({ ...form, pattern: e.target.value })}
                placeholder="ex: PPC RELAXAT"
                data-testid="rule-form-pattern"
              />
            </div>
            <div>
              <Label className="text-xs">Mod de potrivire</Label>
              <Select value={form.match_type} onValueChange={(v) => setForm({ ...form, match_type: v })}>
                <SelectTrigger data-testid="rule-form-match-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="exact">Exact (egalitate case-insensitive)</SelectItem>
                  <SelectItem value="contains">Conține (substring)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Tip clasificare</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="rule-form-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KIND_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Sursă</Label>
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger data-testid="rule-form-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SOURCE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Note (opțional)</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="ex: ofertă PPC introdusă feb 2026"
                data-testid="rule-form-notes"
              />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
              Regulă activă
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Anulează</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="rule-form-save">
              {saving ? "Se salvează…" : "Salvează regula"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
